import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    // Listen for dialog close events to maintain cart icon state
    const dialog = this.querySelector('dialog');
    if (dialog) {
      dialog.addEventListener('close', this.#handleDialogClose);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    
    const dialog = this.querySelector('dialog');
    if (dialog) {
      dialog.removeEventListener('close', this.#handleDialogClose);
    }
  }

  #handleCartAdd = async (event) => {
    const cartItemsElement = this.querySelector('cart-items-component');
    if (!cartItemsElement) return;

    const sectionId = cartItemsElement.dataset.sectionId;
    
    // 1. Try to get HTML from the event payload (Standard behavior)
    let newHtmlString = event.detail.sections && event.detail.sections[sectionId];

    // 2. FALLBACK FIX: If the event didn't contain the HTML for this section, fetch it manually.
    // This happens often when transitioning from Empty -> Populated states.
    if (!newHtmlString && sectionId) {
      try {
        const response = await fetch(`${window.location.pathname}?section_id=${sectionId}`);
        if (response.ok) {
           newHtmlString = await response.text();
        }
      } catch (e) {
        console.error('Failed to fetch cart drawer update:', e);
      }
    }

    // 3. If we have the new HTML, parse and swap it
    if (newHtmlString) {
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(newHtmlString, 'text/html');
      const newCartItemsElement = newDoc.querySelector('cart-items-component');

      if (newCartItemsElement) {
        // Swap the inner HTML to update Header, Items, and Footer
        cartItemsElement.innerHTML = newCartItemsElement.innerHTML;
        
        // Re-initialize any scripts or plugins if necessary (usually not needed for simple HTML swaps)
        // Check if we need to remove specific classes that might linger from empty state
        cartItemsElement.classList.remove('is-empty'); 
      }
    }

    // 4. Update header cart icon count
    this.#updateHeaderCartCount(event.detail.cart || event.detail);

    // 5. Open the drawer
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Handles dialog close event to ensure cart icon stays updated
   */
  #handleDialogClose = async () => {
    try {
      const response = await fetch('/cart.js');
      if (response.ok) {
        const cartData = await response.json();
        this.#updateHeaderCartCount(cartData);
      }
    } catch (error) {
      console.error('Error fetching cart on dialog close:', error);
    }
  };

  /**
   * Updates the cart count bubble in the header
   * @param {Object} cart - Cart object with item_count property
   */
  #updateHeaderCartCount = (cart) => {
    const cartIcons = document.querySelectorAll('.header-actions__cart-icon');
    
    cartIcons.forEach(icon => {
      // Remove existing bubble
      const existingBubble = icon.querySelector('.cart-bubble');
      if (existingBubble) {
        existingBubble.remove();
      }
      
      // Add new bubble if cart has items
      if (cart && cart.item_count > 0) {
        const bubble = document.createElement('div');
        bubble.className = 'cart-bubble';
        
        const bubbleBackground = document.createElement('div');
        bubbleBackground.className = 'cart-bubble__background';
        
        const bubbleText = document.createElement('span');
        bubbleText.className = 'cart-bubble__text';
        bubbleText.textContent = cart.item_count;
        
        bubble.appendChild(bubbleBackground);
        bubble.appendChild(bubbleText);
        icon.appendChild(bubble);
        
        // Add the cart icon modifier class
        icon.classList.add('header-actions__cart-icon--has-cart');
        
        // Store the cart count on the icon element as a data attribute
        // This helps other code know the current state
        icon.dataset.cartCount = cart.item_count;
      } else {
        // Remove the modifier class if cart is empty
        icon.classList.remove('header-actions__cart-icon--has-cart');
        delete icon.dataset.cartCount;
      }
    });
  };

  open() {
    this.showDialog();
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}